import React, { useMemo, useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import useBlueRate from "../hooks/useBlueRate";
import { getLeadSourceLine } from "../utils/waSource";
import NotFound from "./NotFound";

import rodilleras from "../catalogo/rodilleras.js";
import rodillerasRig from "../catalogo/rodilleras-rig.js";
import cintos from "../catalogo/cinturones.js";
import munequeras from "../catalogo/muñequeras.js";
import singlets from "../catalogo/singlets.js";
import vendas from "../catalogo/vendas.js";
import calzado from "../catalogo/calzado.js";

import Navbar from "../components/Navbar";
import RelatedProducts from "../components/RelatedProducts";
import Footer from "../components/Footer";
import "../product.css";

const allProducts = [
  ...rodilleras,
  ...rodillerasRig,
  ...cintos,
  ...munequeras,
  ...singlets,
  ...vendas,
  ...calzado,
];

const slugify = (text) =>
  text
    ?.toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "")
    .replace(/--+/g, "-")
    .trim();

const ProductDetail = () => {
  const blueRate = useBlueRate();
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const colorParam = searchParams.get("color");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const product = useMemo(
    () => allProducts.find((p) => p.slug === slug),
    [slug]
  );

  const isGenderProduct = product?.variants?.some(v => v.gender);

  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedGender, setSelectedGender] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedLength, setSelectedLength] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showSizeGuide, setShowSizeGuide] = useState(false);

  useEffect(() => {
    if (!product) return;

    // Rigidez + longitud
    if (product.types && product.lengths) {
      setSelectedType(product.types[0]);
      setSelectedLength(product.lengths[0]);
    } else if (product.lengths) {
      setSelectedLength(product.lengths[0]);
    }

    if (product.variants?.length > 0) {
      let initialVariant = product.variants[0];

      if (colorParam) {
        const found = product.variants.find(
          (v) => slugify(v.color) === colorParam
        );
        if (found) initialVariant = found;
      }

      setSelectedVariant(initialVariant);

      if (initialVariant.gender) {
        setSelectedGender(initialVariant.gender);
      }

      setActiveImageIndex(0);
    } else {
      setActiveImageIndex(0);
    }

    if (product.sizes) {
      setSelectedSize(product.sizes[0]);
    }

  }, [product, colorParam]);

    const currentImages = useMemo(() => {
      if (selectedVariant?.images?.length) return selectedVariant.images;
      if (selectedVariant?.image) return [selectedVariant.image];
      if (product?.images?.length) return product.images;
      if (product?.image) return [product.image];
      return [];
    }, [product, selectedVariant]);

    if (!product) return <NotFound />;

    const isAvancus = product.brand === "AVANCUS";
    const mainImage = currentImages[activeImageIndex] || null;

  /* ================= PRECIO ================= */

  let price = product.price;
  let oldPrice = product.oldPrice;
  const oldPriceOf = (variant) =>
    (variant || product.variants?.[0])?.oldPrice || product.oldPrice;

  if (product.types && product.lengths) {
    const realVariant = product.variants.find(
      (v) =>
        v.type === selectedType &&
        v.length === selectedLength
    );
    price = realVariant?.price || product.variants[0]?.price;
    oldPrice = oldPriceOf(realVariant);
  }

  else if (product.lengths && !product.types) {
    const realVariant = product.variants.find(
      (v) => v.length === selectedLength
    );
    price = realVariant?.price || product.variants[0]?.price;
    oldPrice = oldPriceOf(realVariant);
  }

  else if (product.variants) {

    if (isGenderProduct && selectedGender) {
      const genderVariant = product.variants.find(
        (v) => v.gender === selectedGender
      );
      price = genderVariant?.price || product.variants[0]?.price;
      oldPrice = oldPriceOf(genderVariant);
    }

    else {
      price = selectedVariant?.price || product.variants[0]?.price;
      oldPrice = oldPriceOf(selectedVariant);
    }
  }

  const hasDiscount = oldPrice > price;
  const discountPercent = hasDiscount
    ? Math.round((1 - price / oldPrice) * 100)
    : 0;

  const reservationUSD = Math.round(price * 0.2);
  const adjustedBlueRate = blueRate ? blueRate * 1.02 : null;
  const reservationARS = adjustedBlueRate
    ? Math.round(reservationUSD * adjustedBlueRate)
    : null;

  /* ================= WHATSAPP ================= */

  const handleWhatsApp = () => {
    const phone = "5491128181122";

    const lines = [
      "¡Hola! Me interesa encargar el siguiente producto:",
      "",
      `• Producto: ${product.name}`,
      selectedVariant?.color && `• Color: ${selectedVariant.color}`,
      isGenderProduct && selectedGender && `• Género: ${selectedGender}`,
      selectedType && `• Rigidez: ${selectedType}`,
      selectedLength && `• Longitud: ${selectedLength} cm`,
      selectedSize && `• Talle: ${selectedSize}`,
      getLeadSourceLine(),
    ].filter(Boolean);

    const message = lines.join("\n");
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleSizeHelp = () => {
  const phone = "5491128181122";

  const lines = [
    "¡Hola! Necesito ayuda para elegir el talle:",
    "",
    `• Producto: ${product.name}`,
    selectedVariant?.color && `• Color: ${selectedVariant.color}`,
    isGenderProduct && selectedGender && `• Género: ${selectedGender}`,
    getLeadSourceLine(),
    ""
  ].filter(Boolean);

  const message = lines.join("\n");
  const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  window.open(url, "_blank", "noopener,noreferrer");
};


  return (
    <>
      <Navbar />

      <div className="product-detail">

        {/* MEDIA */}
        <div className="product-detail__media">
          {mainImage && (
            <div className="product-detail__gallery">

              <div className="product-detail__main-image-wrapper">

                {currentImages.length > 1 && (
                  <button
                    type="button"
                    className="product-detail__nav product-detail__nav--prev"
                    onClick={() =>
                      setActiveImageIndex((prev) =>
                        prev === 0 ? currentImages.length - 1 : prev - 1
                      )
                    }
                  >
                    ‹
                  </button>
                )}

                <img
                  src={mainImage}
                  alt={`${product.name} ${activeImageIndex + 1}`}
                  className="product-detail__main-img"
                />

                {currentImages.length > 1 && (
                  <button
                    type="button"
                    className="product-detail__nav product-detail__nav--next"
                    onClick={() =>
                      setActiveImageIndex((prev) =>
                        prev === currentImages.length - 1 ? 0 : prev + 1
                      )
                    }
                  >
                    ›
                  </button>
                )}
              </div>

              {currentImages.length > 1 && (
                <div className="product-detail__thumbnails">
                  {currentImages.map((img, index) => (
                    <button
                      key={img}
                      type="button"
                      className={`product-detail__thumbnail ${
                        activeImageIndex === index ? "active" : ""
                      }`}
                      onClick={() => setActiveImageIndex(index)}
                    >
                      <img
                        src={img}
                        alt={`thumb ${index}`}
                        className="product-detail__thumbnail-img"
                      />
                    </button>
                  ))}
                </div>
              )}

            </div>
          )}
        </div>

        {/* INFO */}
        <div className="product-detail__info">

          <h1 className="product-detail__title">
            {product.name}
          </h1>

          {!isAvancus && (
            <>
              {hasDiscount ? (
                <div className="product-detail__price product-detail__price--sale">
                  <del className="product-detail__price-old">US$ {oldPrice}</del>
                  <span className="product-detail__price-new">US$ {price}</span>
                  <span className="product-detail__discount">-{discountPercent}%</span>
                </div>
              ) : (
                <div className="product-detail__price">
                  US$ {price}
                </div>
              )}

              <p className="product-detail__reserve">
                Encarga abonando el 20%
                (US$ {reservationUSD}
                {reservationARS && (
                  <> · ${reservationARS.toLocaleString("es-AR")}</>
                )})
              </p>
            </>
          )}


          {/* COLOR */}
          {product.variants?.some(v => v.color) && (
            <div className="variant-group">
              <div className="variant-label">
                Color:
                {selectedVariant?.color && (
                  <span className="variant-selected" translate="no">
                    {" "}{selectedVariant.color}
                  </span>
                )}
              </div>
              <div className="variant-options">
                {product.variants.map((variant) => (
                  <button
                    key={variant.color || variant.images?.[0] || variant.image}
                    className={`variant-btn variant-btn--image ${
                      selectedVariant?.color === variant.color ? "active" : ""
                    }`}
                    onClick={() => {
                      setSelectedVariant(variant);
                      setActiveImageIndex(0);

                      if (
                        variant.color &&
                        slugify(variant.color) !== colorParam
                      ) {
                        setSearchParams(
                          { color: slugify(variant.color) },
                          { replace: true }
                        );
                      }
                    }}
                  >
                  <img
                    src={variant.images?.[0] || variant.image}
                    alt={variant.color || "Variante"}
                    className="variant-img"
                  />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* GENERO (solo singlets) */}
          {isGenderProduct && (
            <div className="variant-group">
              <div className="variant-label">Género</div>
              <div className="variant-options">
                {product.variants.map((variant) => (
                  <button
                    key={variant.gender}
                    className={`variant-btn variant-btn--text ${
                      selectedGender === variant.gender ? "active" : ""
                    }`}
                    onClick={() => {
                      setSelectedGender(variant.gender);
                      setSelectedVariant(variant);
                      setActiveImageIndex(0);
                      setSelectedSize(null);
                    }}
                  >
                    {variant.gender}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* LONGITUD EN VARIANTS (VENDAS) */}
          {product.variants?.some(v => v.length) && !product.lengths && (
            <div className="variant-group">
              <div className="variant-label">Longitud</div>
              <div className="variant-options">
                {product.variants.map((variant) => (
                  <button
                    key={variant.length}
                    className={`variant-btn variant-btn--text ${
                      selectedVariant?.length === variant.length ? "active" : ""
                    }`}
                    onClick={() => {
                      setSelectedVariant(variant);
                      setActiveImageIndex(0);
                    }}
                  >
                    {variant.length}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* RIGIDEZ */}
          {product.types && (
            <div className="variant-group">
              <div className="variant-label">Rigidez</div>
              <div className="variant-options">
                {product.types.map((type) => (
                  <button
                    key={type}
                    className={`variant-btn variant-btn--text ${
                      selectedType === type ? "active" : ""
                    }`}
                    onClick={() => setSelectedType(type)}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* LONGITUD NORMAL */}
          {product.lengths && (
            <div className="variant-group">
              <div className="variant-label">Longitud</div>
              <div className="variant-options">
                {product.lengths.map((length) => (
                  <button
                    key={length}
                    className={`variant-btn variant-btn--text ${
                      selectedLength === length ? "active" : ""
                    }`}
                    onClick={() => setSelectedLength(length)}
                  >
                    {length} cm
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* TALLES */}
          {isGenderProduct && selectedGender && selectedVariant?.sizes ? (

            <div className="variant-group">

              <div className="variant-label">
                {product.slug?.includes("nike-romaleos") ? "Talle (ARG)" : "Talle"}
              </div>

              <div className="variant-options">
                {selectedVariant.sizes.map((size) => (
                  <button
                    key={size}
                    className={`variant-btn variant-btn--text ${
                      selectedSize === size ? "active" : ""
                    }`}
                    onClick={() => setSelectedSize(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>

              {product.sizeGuide && (
                <button
                  type="button"
                  className="size-guide-btn"
                  onClick={() => setShowSizeGuide(true)}
                >
                  Tabla de talles
                </button>
              )}

            </div>

          ) : product.sizes && (

            <div className="variant-group">

              <div className="variant-label">
                {product.slug?.includes("nike-romaleos") ? "Talle (ARG)" : "Talle"}
              </div>

              <div className="variant-options">
                {product.sizes.map((size) => (
                  <button
                    key={size}
                    className={`variant-btn variant-btn--text ${
                      selectedSize === size ? "active" : ""
                    }`}
                    onClick={() => setSelectedSize(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>

              {product.sizeGuide && (
                <button
                  type="button"
                  className="size-guide-btn"
                  onClick={() => setShowSizeGuide(true)}
                >
                  Tabla de talles
                </button>
              )}

            </div>
          )}
          {!isAvancus && (
          <button
            type="button"
            className="size-help-btn"
            onClick={handleSizeHelp}
          >
            ¿Dudas con el talle? Te asesoramos en 2 minutos →
          </button>
        )}

          {isAvancus ? (
            <div className="avancus-redirect-box">
              <p className="avancus-redirect-text">
                Este producto se comercializa exclusivamente a través de
                <strong> Avancus Argentina</strong>.
              </p>

              <a
                href={`https://liftingshoes.com.ar/collections/all  `}
                target="_blank"
                rel="noopener noreferrer"
                className="avancus-redirect-btn"
              >
                Comprar en la tienda oficial →
              </a>
            </div>
          ) : (
            <button
              className="product-detail__cta"
              onClick={handleWhatsApp}
            >
              Comprar ahora
            </button>
          )}


        </div>
      </div>
      {showSizeGuide && (
      <div
        className="size-guide-modal"
        onClick={() => setShowSizeGuide(false)}
      >
        <div
          className="size-guide-content"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="size-guide-close"
            onClick={() => setShowSizeGuide(false)}
          >
            ×
          </button>
          <img
            src={product.sizeGuide}
            alt="Guía de talles"
            className="size-guide-image"
          />
        </div>
      </div>
    )}
      <RelatedProducts
        products={allProducts}
        currentProduct={product}
      />
      <Footer />
    </>
  );
};

export default ProductDetail;
